import { memo } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import type { GenerationEntry } from '../useCanvasStore';
import { getAssetSrc } from '../../utils/assetUrl';

interface GenerationStripProps {
  generations: GenerationEntry[];
  selectedIndex: number;
  onSelect: (index: number) => void;
  type?: 'image' | 'video';
}

function GenerationStrip({ generations, selectedIndex, onSelect, type = 'image' }: GenerationStripProps) {
  if (generations.length <= 1) return null;

  return (
    <Box sx={{ width: '100%' }}>
      {/* Navigation row */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
        <IconButton
          size="small"
          onClick={() => onSelect(Math.max(0, selectedIndex - 1))}
          disabled={selectedIndex === 0}
          sx={{ p: 0.25 }}
        >
          <ChevronLeft sx={{ fontSize: 16 }} />
        </IconButton>
        <Typography variant="caption" color="text.secondary">
          {selectedIndex + 1} / {generations.length}
        </Typography>
        <IconButton
          size="small"
          onClick={() => onSelect(Math.min(generations.length - 1, selectedIndex + 1))}
          disabled={selectedIndex === generations.length - 1}
          sx={{ p: 0.25 }}
        >
          <ChevronRight sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      {/* Thumbnail strip */}
      <Box
        className="nodrag nowheel"
        sx={{
          display: 'flex',
          gap: 0.5,
          overflowX: 'auto',
          pb: 0.5,
          '&::-webkit-scrollbar': { height: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'action.disabled', borderRadius: 2 },
        }}
      >
        {generations.map((gen, idx) => {
          const src = type === 'video' ? gen.video : gen.image;
          if (!src) return null;

          return (
            <Tooltip key={idx} title={gen.prompt ? `${gen.prompt.slice(0, 60)}…` : `Generation ${idx + 1}`} arrow>
              <Box
                onClick={() => onSelect(idx)}
                sx={{
                  flexShrink: 0,
                  width: 40,
                  height: 40,
                  borderRadius: 0.75,
                  overflow: 'hidden',
                  border: '2px solid',
                  borderColor: idx === selectedIndex ? 'primary.main' : 'divider',
                  cursor: 'pointer',
                  opacity: idx === selectedIndex ? 1 : 0.6,
                  transition: 'all 0.15s',
                  '&:hover': { opacity: 1, borderColor: 'primary.light' },
                }}
              >
                {type === 'video' ? (
                  <video
                    src={src}
                    muted
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <img
                    src={getAssetSrc(src, gen.mimeType || 'image/png')}
                    alt={`Gen ${idx + 1}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Box>
    </Box>
  );
}

export default memo(GenerationStrip);
